import { ComponentType, ReactNode, Suspense } from "react";
import { MemoryRouter, MemoryRouterProps } from "react-router";
import { createGlobalStyle } from "styled-components/macro";

import { Provider as SidePanelProvider } from "./SidePanel";
import { Provider as ToastProvider } from "../hooks/use-toast";
import { StackContextProvider } from "../hooks/use-stack-context";
import { TitleProvider } from "../hooks/use-title";
import { PaywallProvider } from "../hooks/use-paywall";
import { Provider as RootHistoryProvider } from "../hooks/use-root-history";
import { Provider as LanguageProvider } from "../hooks/use-language";
import { Provider as QueryClientProvider } from "../hooks/use-query";

function AppProvider({
  children,
  router: Router = MemoryRouter,
  route,
  addStyling = true,
}: {
  children: ReactNode;
  route?: string;
  router?: ComponentType<MemoryRouterProps>;
  addStyling?: boolean;
}) {
  return (
    <Suspense fallback={null}>
      <QueryClientProvider>
        <StackContextProvider>
          <LanguageProvider>
            <TitleProvider>
              <ToastProvider>
                <SidePanelProvider>
                  {addStyling && <GlobalStyle />}
                  <GlueStyles />
                  <Router initialEntries={route ? [route] : undefined}>
                    <RootHistoryProvider>
                      <PaywallProvider>{children}</PaywallProvider>
                    </RootHistoryProvider>
                  </Router>
                </SidePanelProvider>
              </ToastProvider>
            </TitleProvider>
          </LanguageProvider>
        </StackContextProvider>
      </QueryClientProvider>
    </Suspense>
  );
}

export default AppProvider;

const GlobalStyle = createGlobalStyle`
  html, body, div, span, applet, object, iframe,
  h1, h2, h3, h4, h5, h6, p, blockquote, pre,
  a, abbr, acronym, address, big, cite, code,
  del, dfn, em, img, ins, kbd, q, s, samp,
  small, strike, strong, sub, sup, tt, var,
  b, u, i, center,
  dl, dt, dd, ol, ul, li,
  fieldset, form, label, legend,
  table, caption, tbody, tfoot, thead, tr, th, td,
  article, aside, canvas, details, embed, 
  figure, figcaption, footer, header, hgroup, 
  menu, nav, output, ruby, section, summary,
  time, mark, audio, video {
    margin: 0;
    padding: 0;
    border: 0;
    font-size: 100%;
  font: inherit;
    vertical-align: baseline;
  }
  /* HTML5 display-role reset for older browsers */
  article, aside, details, figcaption, figure, 
  footer, header, hgroup, menu, nav, section {
    display: block;
  }
  body {
    line-height: 1;
  }
  ol, ul {
    list-style: none;
  }
  blockquote, q {
    quotes: none;
  }
  blockquote:before, blockquote:after,
  q:before, q:after {
    content: '';
    content: none;
  }
  table {
    border-collapse: collapse;
    border-spacing: 0;
  }
  html {
    box-sizing: border-box;
  }
  *, *:before, *:after {
    box-sizing: inherit;
  }
  body {
    color: #333;
    font-family: -apple-system,system-ui,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",sans-serif;
    font-size: 14px;
    text-rendering: optimizeLegibility;
  }
  html {
    position: fixed;
    height: 100%;
    overflow: hidden;
  }
  body {
    width: 100vw;
    height: 100%;
    overflow-y: auto;
    overflow-x: hidden;
    -webkit-overflow-scrolling: touch;
  }
  
  [data-reach-combobox-popover] {
    z-index: 1000000000;
  }
  [data-tag="app"] {
    height: 100%;
  }
`;

const GlueStyles = createGlobalStyle`
  [data-split],[data-tag],[data-reach-tooltip] {
    font-family: -apple-system,system-ui,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",sans-serif;
    font-size: 14px;
    text-rendering: optimizeLegibility;
  }
  textarea {
    font-family: -apple-system,system-ui,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",sans-serif;
  }
  *:focus {
    outline: none;
  }
  body.accessibility *:not([data-slate-editor='true']):focus {
    outline: 2px solid #7aacfe !important; /* for non-webkit browsers */
    outline: 5px auto -webkit-focus-ring-color !important;
  }
  input:not([type=checkbox]):not([type=radio]):not([type=range]), textarea {
    -webkit-appearance: none;
  }
  * {
    -webkit-tap-highlight-color: transparent;
  }
`;

document.addEventListener(
  "keydown",
  (e: KeyboardEvent) => {
    if (e.key === "Tab" && (e.target as HTMLElement).nodeName !== "INPUT") {
      document.body.classList.add("accessibility");
    } else if (e.key !== " " && e.key !== "Enter" && e.key !== "Escape") {
      document.body.classList.remove("accessibility");
    }
  },
  true
);

document.addEventListener("touchstart", (e: Event) => {
  let target = e.target as HTMLElement | null;
  while (target) {
    if (target === document.body) return;
    target.classList.add("touched");
    target = target.parentElement;
  }
});

document.addEventListener("touchend", () => {
  Array.from(document.querySelectorAll(".touched")).forEach((element) => {
    element.classList.remove("touched");
  });
});

if ("ontouchstart" in window) {
  document.body.classList.add("touch");
}
